import { useState } from 'react';
import { LoginRequest } from '@exam-prep/contracts';
import { t } from '@exam-prep/i18n';
import { useAuth, type Me } from '../../lib/auth';
import { post } from '../../lib/api';
import { Button } from '@exam-prep/ui';
import { Field, Input } from '../../components/FormFields';
import { ErrorBox } from '../../components/Feedback';
import { SubmitButton, ValidatedForm } from '../../components/ValidatedForm';
import { ruleFor } from '../../lib/validation/field-rule';
import { Shell, CodeForm } from './ui/Login';
import { Enrollment } from './tabs/Enrollment';

const emailRule = ruleFor(LoginRequest, 'email');
const passwordRule = ruleFor(LoginRequest, 'password');

export function LoginPage() {
  const { me, setMe, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (me?.stage === 'pending_totp')
    return (
      <Shell
        title="Two-factor authentication"
        description="Enter the code from your authenticator app."
      >
        <CodeForm
          cta="Verify"
          onSubmit={async (code) => {
            setMe(await post<Me>('/admin/auth/totp', { code }));
          }}
        />
        <Button variant="ghost" className="mt-2 w-full" onClick={() => void logout()}>
          Use another account
        </Button>
      </Shell>
    );

  if (me?.stage === 'pending_enrollment')
    return (
      <Shell
        title="Set up two-factor authentication"
        description="Admins must use an authenticator app to sign in."
      >
        <Enrollment onDone={setMe} />
      </Shell>
    );

  const submit = () => {
    setBusy(true);
    setError(null);
    post<Me>('/admin/auth/login', { email: email.trim(), password })
      .then(setMe)
      .catch(setError)
      .finally(() => {
        setBusy(false);
        setPassword('');
      });
  };

  return (
    <Shell title={t('admin.login.title')} description="Use your admin email and password.">
      <ValidatedForm className="flex flex-col gap-3" onValidSubmit={submit}>
        <Field label="Email">
          <Input
            rule={emailRule}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </Field>
        <Field label="Password">
          <Input
            rule={passwordRule}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />
        </Field>
        <ErrorBox error={error} />
        <SubmitButton disabled={busy} pending={busy} pendingOverlay="Signing in…">
          {busy ? 'Signing in…' : 'Sign in'}
        </SubmitButton>
      </ValidatedForm>
    </Shell>
  );
}
